import type { ApplicationDetail } from "@/api/applications";
import type {
  RfqFilters,
  SnapshotApplicationInput,
  SnapshotSelectedOptionInput,
  TaskSnapshotInput,
} from "@/api/tasks";
import type {
  DocApplicationInput,
  DocAssumptions,
  DocSelectedOption,
} from "@/api/quoteDocuments";
import type { UserAssumptionsDto } from "@penn/web-common";

// =============================================================================
// Maps the customer selector's `ApplicationDetail` rows into the wire shapes
// the quotation backend expects: snapshot applications (POST /tasks/:id/snapshot)
// and document applications (cost-analysis / ROI / ad-hoc quote).
// =============================================================================

/** ApplicationDetail[] → snapshot `applications[]` (selection freeze). */
export function toSnapshotApplications(
  details: ApplicationDetail[],
): SnapshotApplicationInput[] {
  return details.map((d) => ({
    appId: d.applicationId,
    // `savedId` is the live library UUID the task side panel uses to fetch the
    // per-application 3D preview + Files (`/v1/applications/:id/files`) and that
    // Open-Task uses to re-apply the apps into the selector. MVP apps are all
    // DB-backed, so it IS the applicationId — previously dropped here, which
    // froze every snapshot row with saved_id=NULL and hid CAD/Files + skipped
    // app re-hydration.
    savedId: d.applicationId,
    name: d.name,
    applicationNumber: d.applicationNumber,
    oem: d.oem,
    productionProgram: d.productionProgram,
    materialCode: d.materialCode,
    annualVolume: d.annualVolume,
    batchSize: d.batchSize,
    fasteners: d.fasteners.map((f) => ({
      fastenerId: f.fastenerId,
      brand: f.brand,
      type: f.type,
      qtyPerPart: f.qtyPerPart,
      qtyPerPartA: f.qtyPerPartA,
      qtyPerPartB: f.qtyPerPartB,
    })),
  }));
}

const numToStr = (v: number | null | undefined): string | undefined =>
  v == null ? undefined : String(v);

/**
 * ApplicationDetail[] → document `applications[]`. Each app must carry ≥1
 * fastener (schema `.min(1)`); apps with no fasteners are dropped. Brand is
 * required by the doc schema, so a null brand falls back to "OTHER".
 */
export function toDocApplications(
  details: ApplicationDetail[],
): DocApplicationInput[] {
  return details
    .filter((d) => d.fasteners.length > 0)
    .map<DocApplicationInput>((d) => ({
      applicationId: d.applicationId,
      name: d.name,
      applicationNumber: d.applicationNumber,
      annualVolume: numToStr(d.annualVolume),
      batchSize: numToStr(d.batchSize),
      fasteners: d.fasteners.map((f) => ({
        brand: f.brand ?? "OTHER",
        type: f.type,
        qtyPerPart: numToStr(f.qtyPerPart),
        qtyPerPartA: numToStr(f.qtyPerPartA),
        qtyPerPartB: numToStr(f.qtyPerPartB),
      })),
    }));
}

/** Snapshot option rows → document option rows (price number → Decimal string). */
export function toDocSelectedOptions(
  opts: SnapshotSelectedOptionInput[],
): DocSelectedOption[] {
  return opts.map((o) => ({
    partNo: o.partNo,
    description: o.description,
    category: o.category,
    toolingType: o.toolingType,
    machineType: o.machineType,
    itemName: o.itemName,
    partName: o.partName,
    price: o.price == null ? null : String(o.price),
    currency: o.currency,
    requirements: o.requirements ?? null,
    qty: o.qty ?? null,
  }));
}

/** The user's saved assumptions are already the document wire shape. */
export function toDocAssumptions(
  dto: UserAssumptionsDto | null | undefined,
): DocAssumptions | undefined {
  if (!dto) return undefined;
  const out: DocAssumptions = { ...dto };
  // Drop empty keys so the backend applies region defaults for missing ones.
  (Object.keys(out) as (keyof DocAssumptions)[]).forEach((k) => {
    if (out[k] == null || out[k] === "") delete out[k];
  });
  return Object.keys(out).length > 0 ? out : undefined;
}

/** Build the RFQ snapshot body for a one-shot submit (POC `source: "config"`). */
export function buildRfqSnapshot(params: {
  solutionProductId: string;
  solutionName?: string | null;
  solutionMachineType?: string | null;
  regionCode: string;
  applications: ApplicationDetail[];
  selectedOptions?: TaskSnapshotInput["selectedOptions"];
  selectedPartNos?: string[];
  /** Filter chips + compare set to freeze (POC parity). Restored by D2. */
  filters?: RfqFilters | null;
}): TaskSnapshotInput {
  const f = params.filters;
  return {
    source: "config",
    createdAtClient: new Date().toISOString(),
    solutionProductId: params.solutionProductId,
    solutionName: params.solutionName ?? null,
    solutionMachineType: params.solutionMachineType ?? null,
    regionCode: params.regionCode,
    machineCostMode: "dedicated",
    // Filter context (omitted when absent → backend stores null/[]). `size` is
    // dropped when "Any" (schema requires a non-empty string or null).
    ...(f?.view ? { filterView: f.view } : {}),
    ...(f?.size ? { filterSize: f.size } : {}),
    ...(f?.pm != null ? { filterPm: f.pm } : {}),
    ...(f?.ri != null ? { filterRi: f.ri } : {}),
    ...(f?.forceMinKn != null ? { filterForceMinKn: f.forceMinKn } : {}),
    ...(f?.forceMaxKn != null ? { filterForceMaxKn: f.forceMaxKn } : {}),
    filterTypes: f?.types ?? [],
    filterCompareIds: f?.compareIds ?? [],
    applications: toSnapshotApplications(params.applications),
    selectedOptions: params.selectedOptions ?? [],
    selectedPartNos: params.selectedPartNos ?? [],
  };
}
